<template>
  <Teleport to="body">
    <div v-if="visible" class="fixed inset-0 z-[9999] flex items-center justify-center p-3">
      <button v-if="!requiredSetup" type="button" class="absolute inset-0 bg-black/50"
        aria-label="Fermer la configuration Shopify" @click="close"></button>
      <div class="relative bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        <div class="shrink-0 p-5 border-b border-gray-200 flex items-start justify-between gap-4">
          <div>
            <h2 class="text-lg font-semibold text-black">Configuration Shopify</h2>
            <p class="text-sm text-gray-600 mt-1">
              {{ requiredSetup ? "Choisissez le genre et le pays de provenance avant le premier import." : "Associez une fois les libellés Shopify à vos valeurs eFashion." }}
            </p>
          </div>
          <button v-if="!requiredSetup" type="button" class="text-gray-500" aria-label="Fermer" @click="close">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="shrink-0 min-h-[52px] border-b border-gray-200 px-5 flex gap-1 overflow-x-auto" role="tablist" aria-label="Configuration Shopify">
          <button v-for="item in tabs" :key="item.id" type="button" role="tab"
            class="shrink-0 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 flex items-center gap-1.5"
            :class="tab === item.id ? 'border-green-600 text-green-700' : 'border-transparent text-gray-600'"
            :disabled="busy || (requiredSetup && item.id !== 'general')"
            :aria-selected="tab === item.id" @click="selectTab(item.id)">
            {{ item.label }}
            <span v-if="item.id !== 'general' && pendingCount(item.id)" class="inline-flex items-center gap-1 text-red-600"
              :aria-label="pendingCount(item.id) + ' correspondances à traiter'">
              <i class="fas fa-exclamation-circle text-xs"></i>
              <span class="text-xs">{{ pendingCount(item.id) }}</span>
            </span>
          </button>
        </div>

        <div class="min-h-0 flex-1 overflow-y-auto p-5 space-y-4">
          <p v-if="message" class="text-sm rounded-lg p-3"
            :class="messageIsError ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'">{{ message }}</p>
          <div v-if="tab === 'general'" class="max-w-xl space-y-4">
            <label class="block text-sm font-medium text-gray-800" for="shopify-default-gender">Genre par défaut</label>
            <select id="shopify-default-gender" v-model="defaultGender"
              class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              <option value="">Choisir un genre</option>
              <option value="femme">Femme</option>
              <option value="homme">Homme</option>
              <option value="enfant">Enfant</option>
              <option value="aucun">Plusieurs genres / aucun genre par défaut</option>
            </select>
            <p class="text-xs text-gray-600">Cette préférence aide à proposer une catégorie. Les catégories ambiguës restent à valider.</p>
            <label class="block text-sm font-medium text-gray-800" for="shopify-default-provenance">Pays de provenance par défaut</label>
            <select id="shopify-default-provenance" v-model.number="defaultProvenanceId"
              class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              <option value="">Choisir un pays</option>
              <option v-for="provenance in provenances" :key="provenance.id" :value="provenance.id">{{ provenance.label }}</option>
            </select>
            <p class="text-xs text-gray-600">Ce pays est utilisé seulement si Shopify ne renseigne pas une provenance reconnue pour le produit.</p>
            <label class="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 cursor-pointer">
              <input
                v-model="autoSyncFromShopify"
                type="checkbox"
                class="mt-0.5 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <span class="min-w-0 flex-1">
                <span class="block text-sm font-medium text-gray-800">
                  Mettre à jour automatiquement les produits depuis Shopify
                </span>
                <span class="mt-0.5 block text-xs text-gray-600">
                  Dès qu’un changement intervient sur Shopify, tous les produits déjà liés sont mis à jour dans eFashion (prix, poids, titre/description, provenance, stock). Les photos ne sont pas resynchronisées automatiquement.
                </span>
                <span
                  v-if="isAdmin"
                  class="mt-3 block rounded-lg border border-orange-300 bg-orange-50 p-3 space-y-2"
                  @click.prevent
                >
                  <span class="block text-xs font-medium text-orange-800">
                    Visible uniquement aux administrateurs
                  </span>
                  <button type="button"
                    class="rounded-lg bg-orange-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50"
                    :disabled="busy" @click="reregisterWebhooks">
                    Réenregistrer les webhooks
                  </button>
                  <span
                    v-if="webhookMessage"
                    class="block rounded-md px-2 py-1.5 text-xs"
                    :class="webhookMessageIsError ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'"
                    role="status"
                  >{{ webhookMessage }}</span>
                  <span v-if="webhookInfo && !webhookMessageIsError" class="block text-xs text-orange-900/80 break-all">{{ webhookInfo }}</span>
                </span>
              </span>
            </label>
            <label class="mt-3 flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 cursor-pointer">
              <input
                v-model="autoSyncOrdersToShopify"
                type="checkbox"
                class="mt-0.5 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <span class="min-w-0 flex-1">
                <span class="block text-sm font-medium text-gray-800">
                  Envoyer automatiquement les commandes vers Shopify
                </span>
                <span class="mt-0.5 block text-xs text-gray-600">
                  Jusqu’à 5 commandes, toutes les 5 minutes.
                </span>
              </span>
            </label>
            <button type="button" class="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              :disabled="busy || !defaultGender || !defaultProvenanceId" @click="saveGeneral">
              {{ busy ? "Enregistrement…" : "Enregistrer la configuration" }}
            </button>
          </div>

          <div v-else class="space-y-5">
            <div class="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
              <span>Les correspondances sont calculées depuis le catalogue enregistré.</span>
              <button type="button" class="text-blue-700 hover:underline disabled:opacity-50"
                :disabled="pendingLoading || busy" @click="loadPending(true)">
                <i class="fas fa-sync-alt mr-1"></i> Recalculer les correspondances
              </button>
            </div>
            <div v-if="pendingLoading" class="text-sm text-gray-500" role="status">
              <i class="fas fa-spinner fa-spin mr-1"></i> Analyse du catalogue enregistré en cours…
            </div>
            <p v-if="!catalogReady" class="text-sm text-blue-700">Le catalogue se synchronise. Les correspondances apparaîtront une fois la copie terminée.</p>
            <p v-if="pendingSummary?.incomplete" class="rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
              Le catalogue est très volumineux : les alertes couvrent les {{ pendingSummary.scannedProducts }} premiers produits.
            </p>
            <div class="space-y-2">
              <h3 class="text-sm font-semibold text-gray-900">À lier {{ pending.length ? "(" + pending.length + ")" : "" }}</h3>
              <p v-if="!pending.length && !pendingLoading" class="text-sm text-green-700">
                {{ pendingSummary ? "Toutes les valeurs détectées sont liées." : "Aucune correspondance détectée pour le moment." }}
              </p>
              <div v-for="label in pending" :key="label"
                class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-3">
                <span class="flex items-center gap-2 text-sm font-medium text-gray-900">
                  <span v-if="tab === 'color' && colorHex(label)" class="inline-block h-6 w-6 shrink-0 rounded-full border border-gray-400 shadow-sm"
                    :style="{ backgroundColor: colorHex(label) || undefined }" :title="colorHex(label) || undefined"
                    :aria-label="'Couleur Shopify ' + colorHex(label)"></span>
                  <span>{{ label }}</span>
                  <span v-if="tab === 'color' && colorHex(label)" class="text-xs font-normal text-gray-600">{{ colorHex(label) }}</span>
                </span>
                <button type="button" class="rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100"
                  :disabled="busy || loading" @click="openPicker(label)">{{ chooseLabel }}</button>
              </div>
            </div>

            <div class="space-y-2">
              <h3 class="text-sm font-semibold text-gray-900">Correspondances enregistrées</h3>
              <div v-if="loading" class="text-sm text-gray-500">Chargement…</div>
              <p v-else-if="!mappings.length" class="text-sm text-gray-500">Aucune correspondance enregistrée.</p>
              <div v-for="row in mappings" :key="row.sourceLabelNormalized"
                class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 p-3">
                <div class="text-sm min-w-0">
                  <span class="font-medium">{{ row.sourceLabel }}</span>
                  <span class="mx-2 text-gray-400">→</span>
                  <span>{{ row.targetLabel || "#" + row.targetId }}</span>
                </div>
                <div class="flex items-center gap-3">
                  <button type="button" class="text-sm text-blue-700 hover:underline" :disabled="busy || loading"
                    @click="openPicker(row.sourceLabel)">Modifier</button>
                  <button type="button" class="text-sm text-red-700 hover:underline" :disabled="busy"
                    @click="remove(row)">Supprimer</button>
                </div>
              </div>
            </div>

            <div class="border-t border-gray-100 pt-3">
              <button type="button" class="text-xs text-gray-600 hover:underline" @click="manualOpen = !manualOpen">
                {{ manualOpen ? "Masquer l'ajout manuel" : "Ajouter un libellé Shopify absent de cette liste" }}
              </button>
              <div v-if="manualOpen" class="mt-3 flex flex-wrap gap-2">
                <input v-model.trim="manualSource" type="text" maxlength="255" aria-label="Libellé Shopify"
                  placeholder="Libellé Shopify" class="flex-1 min-w-[180px] rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                <button type="button" class="rounded-lg bg-gray-800 px-3 py-2 text-sm text-white disabled:opacity-50"
                  :disabled="!manualSource || busy || loading" @click="openPicker(manualSource)">Choisir une valeur</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>

  <CouleursPickerModal v-model:isVisible="colorPickerVisible" :couleurs-list="officialColors"
    :official-only="true" :selected-ids="[]" @confirm="onColorConfirm" />

  <Teleport to="body">
    <div v-if="pickerVisible" class="fixed inset-0 z-[10050] flex items-center justify-center p-3">
      <button type="button" class="absolute inset-0 bg-black/50" aria-label="Fermer le choix"
        @click="pickerVisible = false"></button>
      <div class="relative flex max-h-[85vh] w-full max-w-xl flex-col rounded-xl bg-white p-5 shadow-xl">
        <div class="flex items-start justify-between gap-4">
          <div>
            <h3 class="font-semibold text-gray-900">{{ chooseLabel }}</h3>
            <p class="text-sm text-gray-600 mt-1">Valeur Shopify : <strong>{{ pickerSource }}</strong></p>
          </div>
          <button type="button" aria-label="Fermer" class="text-gray-500" @click="pickerVisible = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <p v-if="messageIsError && message" class="mt-3 rounded-lg bg-red-50 p-2 text-sm text-red-700">{{ message }}</p>

        <div v-if="tab === 'category'" class="mt-5">
          <CategoryTreeAutocomplete :tree="categoriesTree" cat-id="" sous-id="" sous-sous-id=""
            label="Catégorie eFashion" placeholder="Rechercher une catégorie feuille…" @select="onCategorySelect" />
        </div>
        <div v-else-if="tab === 'composition'" class="mt-5 flex min-h-0 flex-col gap-3">
          <input v-model.trim="pickerSearch" type="search" aria-label="Rechercher une matière"
            placeholder="Rechercher une matière" class="rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          <div class="min-h-0 overflow-y-auto rounded-lg border border-gray-200">
            <button v-for="target in filteredTargets" :key="target.id" type="button"
              class="block w-full border-b border-gray-100 px-3 py-2 text-left text-sm hover:bg-green-50"
              :disabled="busy" @click="savePickedTarget(target.id)">{{ target.label }}</button>
            <p v-if="!filteredTargets.length" class="p-3 text-sm text-gray-500">Aucune matière trouvée.</p>
          </div>
        </div>
        <div v-else-if="tab === 'brand'" class="mt-5 min-h-0 overflow-y-auto space-y-3">
          <div class="rounded-lg border border-gray-200">
            <button type="button" class="w-full px-3 py-2 text-left text-sm font-medium flex justify-between"
              @click="brandListOpen = !brandListOpen">
              Choisir une marque existante ({{ targets.length }})
              <i class="fas fa-chevron-down text-xs"></i>
            </button>
            <div v-if="brandListOpen" class="border-t border-gray-200 p-3 space-y-2">
              <input v-model.trim="pickerSearch" type="search" aria-label="Rechercher une marque"
                placeholder="Rechercher une marque" class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              <div class="max-h-52 overflow-y-auto">
                <button v-for="target in filteredTargets" :key="target.id" type="button"
                  class="block w-full rounded-md px-3 py-2 text-left text-sm hover:bg-green-50"
                  :disabled="busy" @click="savePickedTarget(target.id)">
                  {{ target.label }} <span class="text-xs text-gray-500">#{{ target.id }}{{ target.isDefault ? " · par défaut" : "" }}</span>
                </button>
                <p v-if="!filteredTargets.length" class="text-sm text-gray-500">Aucune marque trouvée.</p>
              </div>
            </div>
          </div>
          <div class="rounded-lg border border-gray-200">
            <button type="button" class="w-full px-3 py-2 text-left text-sm font-medium flex justify-between"
              @click="brandCreateOpen = !brandCreateOpen">
              Créer une nouvelle marque <i class="fas fa-chevron-down text-xs"></i>
            </button>
            <div v-if="brandCreateOpen" class="border-t border-gray-200 p-3 space-y-2">
              <label for="shopify-new-brand" class="block text-xs text-gray-600">Nom de la nouvelle marque</label>
              <input id="shopify-new-brand" v-model.trim="brandName" maxlength="255" type="text"
                class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              <button type="button" class="rounded-lg bg-green-600 px-3 py-2 text-sm text-white disabled:opacity-50"
                :disabled="busy || !brandName" @click="createBrand">Créer et lier</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import CouleursPickerModal from "@/components/mel/CouleursPickerModal.vue";
import CategoryTreeAutocomplete from "@/components/forms/CategoryTreeAutocomplete.vue";
import { getCouleursOfficielles } from "@/api/couleurs";
import { getCategoriesTree } from "@/api/categories";
import {
  createShopifyBrandMapping, deleteShopifyMapping, getShopifyMappingTargets,
  getShopifyMappings, getShopifyPendingMappings, getShopifySettings, getShopifyProvenances,
  reregisterShopifyWebhooks, saveShopifyMapping, saveShopifySettings,
  type ShopifyMapping, type ShopifyMappingTarget, type ShopifyMappingType,
  type ShopifyPendingMappings, type ShopifySettings,
} from "@/api/shopify";
import { useAuthStore } from "@/stores/auth";
import type { CouleurAPI } from "@/types/mel-components";
import type { CategorieNode, CategoryPathSelection } from "@/types/categorie";

type Tab = "general" | ShopifyMappingType;
const props = defineProps<{
  visible: boolean;
  shopUrl?: string;
  requiredSetup: boolean;
  catalogReady: boolean;
  catalogRevision: number;
  pendingColors: string[];
  pendingCategories: string[];
  pendingCompositions: string[];
  pendingBrands: string[];
}>();
const emit = defineEmits<{
  (event: "update:visible", value: boolean): void;
  (event: "changed"): void;
  (event: "settings-saved", value: ShopifySettings): void;
}>();

const authStore = useAuthStore();
const isAdmin = computed(() => authStore.isImpersonating);

const tabs: Array<{ id: Tab; label: string }> = [
  { id: "general", label: "Général" },
  { id: "color", label: "Couleurs" },
  { id: "category", label: "Catégories" },
  { id: "composition", label: "Compositions" },
  { id: "brand", label: "Marques" },
];
const tab = ref<Tab>("general");
const defaultGender = ref<"" | NonNullable<ShopifySettings["defaultGender"]>>("");
const savedGender = ref<ShopifySettings["defaultGender"]>(null);
const defaultProvenanceId = ref<number | "">("");
const autoSyncFromShopify = ref(false);
const autoSyncOrdersToShopify = ref(true);
const webhookInfo = ref("");
const webhookMessage = ref("");
const webhookMessageIsError = ref(false);
const savedProvenanceId = ref<number | null>(null);
const provenances = ref<Array<{ id: number; label: string }>>([]);
const mappings = ref<ShopifyMapping[]>([]);
const targets = ref<ShopifyMappingTarget[]>([]);
const pendingSummary = ref<ShopifyPendingMappings | null>(null);
const pendingLoading = ref(false);
const loading = ref(false);
const busy = ref(false);
const message = ref("");
const messageIsError = ref(false);
const pickerVisible = ref(false);
const colorPickerVisible = ref(false);
const pickerSource = ref("");
const pickerSearch = ref("");
const brandName = ref("");
const brandListOpen = ref(true);
const brandCreateOpen = ref(false);
const manualOpen = ref(false);
const manualSource = ref("");
const officialColors = ref<CouleurAPI[]>([]);
const categoriesTree = ref<CategorieNode[]>([]);
let loadSequence = 0;
let pendingSequence = 0;
const resolvedDuringScan = new Map<ShopifyMappingType, Set<string>>();

const pendingByType = computed(() => pendingSummary.value || {
  color: props.pendingColors, category: props.pendingCategories,
  composition: props.pendingCompositions, brand: props.pendingBrands,
  colorHexByName: {},
  scannedProducts: 0, incomplete: false,
});
const pending = computed(() => tab.value === "general" ? [] : pendingByType.value[tab.value]);
const chooseLabel = computed(() => tab.value === "color" ? "Choisir une couleur" :
  tab.value === "category" ? "Choisir une catégorie" :
  tab.value === "composition" ? "Choisir une matière" : "Choisir une marque");
const filteredTargets = computed(() => {
  const search = normalize(pickerSearch.value);
  const rows = search ? targets.value.filter((target) => normalize(target.label).includes(search)) : targets.value;
  if (tab.value !== "brand" || search) return rows;
  const source = normalize(pickerSource.value);
  const sourceWords = new Set(source.split(" ").filter((word) => word.length > 2 && word !== "b2b"));
  return [...rows].sort((left, right) => {
    const score = (target: ShopifyMappingTarget) =>
      normalize(target.label).split(" ").filter((word) => sourceWords.has(word)).length +
      (normalize(target.label) === source ? 10 : 0);
    return score(right) - score(left) || Number(right.isDefault) - Number(left.isDefault) ||
      left.label.localeCompare(right.label, "fr");
  });
});

function normalize(value: string) {
  return String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}
function pendingCount(type: Tab) {
  return type === "general" ? 0 : pendingByType.value[type].length;
}
function colorHex(label: string): string | null {
  const hex = pendingByType.value.colorHexByName[normalize(label)];
  return /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex || "") ? hex : null;
}
function clearPending(type: ShopifyMappingType, source: string) {
  if (pendingLoading.value) {
    const resolved = resolvedDuringScan.get(type) || new Set<string>();
    resolved.add(normalize(source));
    resolvedDuringScan.set(type, resolved);
  }
  if (pendingSummary.value) {
    pendingSummary.value = {
      ...pendingSummary.value,
      [type]: pendingSummary.value[type].filter((label) => normalize(label) !== normalize(source)),
    };
  }
}
function showMessage(value: string, isError = false) {
  message.value = value;
  messageIsError.value = isError;
}
function close() {
  if (!props.requiredSetup && !busy.value) emit("update:visible", false);
}
async function loadPending(force = false) {
  if (!props.catalogReady) return;
  if ((pendingLoading.value || pendingSummary.value) && !force) return;
  const sequence = ++pendingSequence;
  resolvedDuringScan.clear();
  pendingLoading.value = true;
  try {
    const summary = await getShopifyPendingMappings();
    if (sequence === pendingSequence) {
      for (const type of ["color", "category", "composition", "brand"] as const) {
        const resolved = resolvedDuringScan.get(type);
        if (resolved) summary[type] = summary[type].filter((label) => !resolved.has(normalize(label)));
      }
      pendingSummary.value = summary;
    }
  } catch (caught) {
    if (sequence === pendingSequence)
      showMessage(caught instanceof Error ? caught.message : "Catalogue Shopify indisponible.", true);
  } finally {
    if (sequence === pendingSequence) {
      pendingLoading.value = false;
      resolvedDuringScan.clear();
    }
  }
}
async function loadTab(type: ShopifyMappingType) {
  const sequence = ++loadSequence;
  loading.value = true;
  try {
    const [nextMappings, nextTargets] = await Promise.all([
      getShopifyMappings(type), getShopifyMappingTargets(type),
    ]);
    if (sequence === loadSequence) {
      mappings.value = nextMappings;
      targets.value = nextTargets;
    }
  } catch (caught) {
    if (sequence === loadSequence)
      showMessage(caught instanceof Error ? caught.message : "Chargement impossible.", true);
  } finally {
    if (sequence === loadSequence) loading.value = false;
  }
}
function selectTab(value: Tab) {
  if (busy.value || (props.requiredSetup && value !== "general")) return;
  tab.value = value;
  pickerVisible.value = false;
  colorPickerVisible.value = false;
  showMessage("");
  if (value === "general") {
    loadSequence += 1;
    loading.value = false;
  } else void loadTab(value);
}
async function saveGeneral() {
  if (!defaultGender.value || !defaultProvenanceId.value || busy.value) return;
  busy.value = true;
  try {
    const settings = await saveShopifySettings(
      defaultGender.value,
      Number(defaultProvenanceId.value),
      autoSyncFromShopify.value,
      autoSyncOrdersToShopify.value,
    );
    const settingChanged = savedGender.value !== settings.defaultGender ||
      savedProvenanceId.value !== settings.defaultProvenanceId;
    savedGender.value = settings.defaultGender;
    savedProvenanceId.value = settings.defaultProvenanceId;
    autoSyncFromShopify.value = Boolean(settings.autoSyncFromShopify);
    autoSyncOrdersToShopify.value = settings.autoSyncOrdersToShopify !== false;
    emit("settings-saved", settings);
    emit("changed");
    showMessage("Configuration enregistrée.");
    if (settingChanged) {
      pendingSummary.value = null;
      if (pendingLoading.value) {
        pendingSequence += 1;
        pendingLoading.value = false;
      }
      if (!props.requiredSetup) void loadPending(true);
    }
  } catch (caught) {
    showMessage(caught instanceof Error ? caught.message : "Enregistrement impossible.", true);
  } finally {
    busy.value = false;
  }
}
async function reregisterWebhooks() {
  if (busy.value) return;
  busy.value = true;
  webhookInfo.value = "";
  webhookMessage.value = "";
  webhookMessageIsError.value = false;
  try {
    const result = await reregisterShopifyWebhooks();
    const topics = result.subscriptions.map((item) => item.topic).join(", ") || "(aucun)";
    webhookInfo.value = `URL : ${result.callbackUrl} — sujets : ${topics}`;
    webhookMessage.value = "Webhooks réenregistrés.";
    webhookMessageIsError.value = false;
  } catch (caught) {
    webhookMessage.value = caught instanceof Error
      ? caught.message
      : "Réenregistrement des webhooks impossible.";
    webhookMessageIsError.value = true;
  } finally {
    busy.value = false;
  }
}
async function openPicker(source: string) {
  if (tab.value === "general" || busy.value || loading.value) return;
  busy.value = true;
  pickerSource.value = source;
  pickerSearch.value = "";
  brandName.value = source;
  brandListOpen.value = true;
  brandCreateOpen.value = false;
  try {
    if (tab.value === "color") {
      if (!officialColors.value.length) officialColors.value = await getCouleursOfficielles();
      colorPickerVisible.value = true;
    } else {
      if (tab.value === "category" && !categoriesTree.value.length)
        categoriesTree.value = await getCategoriesTree();
      pickerVisible.value = true;
    }
  } catch (caught) {
    showMessage(caught instanceof Error ? caught.message : "Sélecteur indisponible.", true);
  } finally {
    busy.value = false;
  }
}
async function savePickedTarget(targetId: number) {
  if (tab.value === "general" || busy.value || !pickerSource.value) return;
  busy.value = true;
  try {
    await saveShopifyMapping(tab.value, pickerSource.value, targetId);
    clearPending(tab.value, pickerSource.value);
    pickerVisible.value = false;
    colorPickerVisible.value = false;
    manualSource.value = "";
    await loadTab(tab.value);
    emit("changed");
    showMessage("Correspondance enregistrée.");
  } catch (caught) {
    showMessage(caught instanceof Error ? caught.message : "Enregistrement impossible.", true);
  } finally {
    busy.value = false;
  }
}
function onColorConfirm(colors: CouleurAPI[]) {
  const color = colors[0];
  colorPickerVisible.value = false;
  if (color) void savePickedTarget(color.id_couleur);
}
function onCategorySelect(path: CategoryPathSelection) {
  const targetId = Number(path.leafId);
  if (targetId) void savePickedTarget(targetId);
}
async function createBrand() {
  if (tab.value !== "brand" || busy.value || !brandName.value) return;
  busy.value = true;
  try {
    const result = await createShopifyBrandMapping(pickerSource.value, brandName.value);
    clearPending("brand", pickerSource.value);
    pickerVisible.value = false;
    manualSource.value = "";
    await loadTab("brand");
    emit("changed");
    showMessage(result.createdBrand ? "Marque créée et liée." : "Marque existante liée.");
  } catch (caught) {
    showMessage(caught instanceof Error ? caught.message : "Création impossible.", true);
  } finally {
    busy.value = false;
  }
}
async function remove(row: ShopifyMapping) {
  if (tab.value === "general" || busy.value) return;
  busy.value = true;
  try {
    await deleteShopifyMapping(tab.value, row.sourceLabel);
    await loadTab(tab.value);
    if (pendingLoading.value) {
      pendingSequence += 1;
      pendingLoading.value = false;
    }
    void loadPending(true);
    emit("changed");
    showMessage("Correspondance supprimée.");
  } catch (caught) {
    showMessage(caught instanceof Error ? caught.message : "Suppression impossible.", true);
  } finally {
    busy.value = false;
  }
}

watch(() => props.visible, async (visible) => {
  if (!visible) return;
  tab.value = "general";
  loadSequence += 1;
  loading.value = false;
  showMessage("");
  try {
    const [settings, provenanceOptions] = await Promise.all([getShopifySettings(), getShopifyProvenances()]);
    provenances.value = provenanceOptions;
    defaultGender.value = settings.defaultGender || "";
    savedGender.value = settings.defaultGender;
    defaultProvenanceId.value = provenanceOptions.some((option) => option.id === settings.defaultProvenanceId)
      ? Number(settings.defaultProvenanceId) : "";
    savedProvenanceId.value = settings.defaultProvenanceId;
    autoSyncFromShopify.value = Boolean(settings.autoSyncFromShopify);
    autoSyncOrdersToShopify.value = settings.autoSyncOrdersToShopify !== false;
    if (!props.requiredSetup) void loadPending();
  } catch (caught) {
    showMessage(caught instanceof Error ? caught.message : "Configuration indisponible.", true);
  }
});
watch(() => props.shopUrl, () => {
  pendingSequence += 1;
  pendingSummary.value = null;
  pendingLoading.value = false;
  resolvedDuringScan.clear();
});
watch(() => props.catalogRevision, () => {
  pendingSummary.value = null;
  if (props.visible && !props.requiredSetup) void loadPending(true);
});
</script>
